import { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography, Grid, CircularProgress, Alert, FormControlLabel, Switch, RadioGroup, Chip } from '@mui/material';
import { useMediaQuery, useTheme, ButtonGroup, Button, colors, Collapse, Radio, TextField } from '@mui/material';
import { Checkbox } from '@mui/material';
import Layout from '../Global/Layout';
import { AddAlarmRounded, SaveRounded } from '@mui/icons-material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { ApprovalIcon, ProcessIcon, AssetIcon, DocIcon } from '../Global/Icons';
import { TaskIcon, AuditIcon, FormIcon, SupportIcon } from '../Global/Icons';
import { TimePicker } from '../Global/Components';
import BlockIcon from '@mui/icons-material/Block';
import DoneIcon from '@mui/icons-material/Done';
import { Comment } from '@mui/icons-material';
import { v4 } from 'uuid';
import moment from 'moment';
import { isEmpty, isEqual } from 'lodash';
import StateManager from '../Global/StateManager';
import { blue } from '@mui/material/colors';

const WORKFLOW = 'workflow';
const AUDIT = 'audit';
const FORM = 'form';
const SUPPORT = 'support';
const APPROVAL = 'approval';
const PROCESS = 'process';
const MENTION = 'mention';
const ASSET = 'asset';
const DL = 'docLibrary';

const descriptions = {
  [WORKFLOW]: 'All workflow related notifications will be sent to you',
  [AUDIT]: 'All audit related notifications will be sent to you',
  [FORM]: 'All form related notifications will be sent to you',
  [SUPPORT]: 'All support related notifications will be sent to you',
  [APPROVAL]: 'All approval related notifications will be sent to you',
  [PROCESS]: 'All process related notifications will be sent to you',
  [MENTION]: 'All mention related notifications will be sent to you',
  [ASSET]: 'All asset related notifications will be sent to you',
  [DL]: 'All document library related notifications will be sent to you',
};

const getIcon = (type) => {
  switch (type) {
    case WORKFLOW:
      return <TaskIcon />;
    case AUDIT:
      return <AuditIcon />;
    case FORM:
      return <FormIcon />;
    case SUPPORT:
      return <SupportIcon />;
    case APPROVAL:
      return <ApprovalIcon />;
    case PROCESS:
      return <ProcessIcon />;
    case ASSET:
      return <AssetIcon />;
    case DL:
      return <DocIcon />;
    case MENTION:
      return <Comment sx={{ color: colors.yellow[700] }} />;
    default:
      return <TaskIcon />;
  }
};

export default function Settings() {
  const theme = useTheme();
  const largeDevices = useMediaQuery(theme.breakpoints.up('sm'));
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedSchedulingSettings, setSavedSchedulingSettings] = useState(null);
  const [savingSchedulingSettings, setSavingSchedulingSettings] = useState(false);
  const [enableScheduling, setEnableScheduling] = useState(false);
  const [schedulingOption, setSchedulingOption] = useState('');
  const [dailyOccurrences, setDailyOccurrences] = useState([]);
  const [hoursInterval, setHoursInterval] = useState(1);
  const [onlyInWorkhours, setOnlyInWorkhours] = useState(false);
  const [dailyTime, setDailyTime] = useState(null);

  const currSchedulingSettings = getCurrSchedulingSettings();
  const schedulingSettingsValid = areSchedulingSettingsValid();
  const settingsChanged = !isEqual(savedSchedulingSettings, currSchedulingSettings);

  function getCurrSchedulingSettings() {
    if (!enableScheduling) return null;

    if (schedulingOption === 'daily') {
      if (isEmpty(dailyOccurrences)) return null;

      return { enableScheduling, schedulingOption, dailyOccurrences, onlyInWorkhours };
    }

    if (schedulingOption === 'hourly') {
      return { enableScheduling, schedulingOption, hoursInterval, onlyInWorkhours };
    }

    return null;
  }

  function areSchedulingSettingsValid() {
    if (!schedulingOption) return true;

    if (!schedulingOption) return false;

    if (schedulingOption === 'daily') {
      return !isEmpty(dailyOccurrences);
    }

    return true;
  }

  useEffect(() => {
    axios
      .get('/notifications/getUserNotificationsSettings')
      .then(({ data }) => {
        setLoading(false);
        setSettings(data.enabledTypes);
        setSavedSchedulingSettings(data.schedulingSettings || null);
        setEnableScheduling(Boolean(data.schedulingSettings));
        setSchedulingOption(data.schedulingSettings?.schedulingOption || '');
        setDailyOccurrences(data.schedulingSettings?.dailyOccurrences || []);
        setHoursInterval(data.schedulingSettings?.hoursInterval || 1);
        setOnlyInWorkhours(data.schedulingSettings?.onlyInWorkhours ?? false);
        setDailyTime(null);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
      });
  }, []);

  function saveSchedulingSettings() {
    if (savingSchedulingSettings) return;

    setSavingSchedulingSettings(true);
    axios
      .post('/notifications/saveNotificationsSchedulingSettings', { schedulingSettings: currSchedulingSettings })
      .then((res) => {
        setSavedSchedulingSettings(res.data.schedulingSettings);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
      })
      .finally(() => {
        setSavingSchedulingSettings(false);
      });
  }

  function switchSetting(type, enabled) {
    let newSettings = settings;
    let index = newSettings.findIndex((x) => x.notificationCategory === type);
    if (index > -1) {
      newSettings[index].enabled = enabled;
      setSettings([...newSettings]);
      axios.post('/notifications/setTypeEnabledSetting', { setting: type, value: enabled }).catch((err) => {
        newSettings[index].enabled = enabled;
        setSettings([...newSettings]);
        console.error(err);
      });
    }
  }

  function disableAllTypes() {
    let newSettings = settings;
    let oldSettings = settings.map((a) => ({ ...a }));
    newSettings.forEach((setting) => (setting.enabled = false));
    setSettings([...newSettings]);
    axios.post('/notifications/disableAllTypes').catch((err) => {
      console.error(err);
      setSettings([...oldSettings]);
    });
  }

  function enableAllTypes() {
    let newSettings = settings;
    let oldSettings = settings.map((a) => ({ ...a }));
    newSettings.forEach((setting) => (setting.enabled = true));
    setSettings([...newSettings]);
    axios.post('/notifications/enableAllTypes').catch((err) => {
      console.error(err);
      setSettings([...oldSettings]);
    });
  }

  function addDailyTime(value) {
    if (!value) return;

    const item = { _id: v4(), text: moment(value).format('HH:mm'), value: moment(value).toDate().toISOString() };
    setDailyTime(null);
    setDailyOccurrences([...dailyOccurrences, item]);
  }

  if (loading) {
    return (
      <Grid container alignItems="center" justifyContent="center" style={{ height: '85vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Layout icon={<SettingsOutlinedIcon style={{ color: '#4a4e54' }} />} title="Notifications Settings" back={true}>
      <Grid container justifyContent="center">
        <Grid
          item
          container
          direction={largeDevices ? 'row' : 'column'}
          style={{ marginBottom: '0.4rem' }}
          justifyContent={largeDevices ? 'space-between' : 'flex-start'}
          alignItems="center"
          lg={8}
          md={10}
          xs={12}
        >
          <Grid item container>
            <Typography variant="h5">Notifications settings</Typography>
          </Grid>

          <Grid item container alignItems={'center'} sx={{ mt: 4 }}>
            <Grid item container alignItems={'center'}>
              <Typography variant="subtitle1">Schedule emails</Typography>
              <Chip label="beta" size="small" sx={{ color: 'white', background: blue[500], ml: 2 }} />
            </Grid>

            <Grid item container alignItems={'center'} sx={{ mt: 1 }}>
              <Alert severity="info">
                You can now schedule your emails so they are all sent in a single email at a certain time of the day.
                This means you won't get email notifications instantly, instead it will be an AI driven summary. This
                won't effect application notifications, this will be for eamils only.
              </Alert>
            </Grid>

            <Grid item container alignItems={'center'} sx={{ mt: 2 }}>
              <FormControlLabel
                label="Enable emails scheduling"
                control={<Switch checked={enableScheduling} />}
                onChange={(e) => setEnableScheduling(e.target.checked)}
              />
            </Grid>

            <Grid item container component={Collapse} in={enableScheduling}>
              <Grid item container direction={'column'}>
                <Typography gutterBottom>Send notification summary emails:</Typography>
                <RadioGroup value={schedulingOption} onChange={(e) => setSchedulingOption(e.target.value)}>
                  <FormControlLabel
                    value="daily"
                    control={<Radio />}
                    label="Send emails at specific times in the day"
                  />
                  <FormControlLabel value="hourly" control={<Radio />} label="Send emails every x hours" />
                </RadioGroup>
              </Grid>

              <Grid item container alignItems={'center'} sx={{ mt: 1 }}>
                <Alert severity="info">No email will be sent if there are no notifications</Alert>
              </Grid>

              {schedulingOption === 'daily' && (
                <>
                  <Grid item container alignItems={'center'} sx={{ mt: 1 }}>
                    <Typography>Select the times of day you would like the email summary to be sent:</Typography>
                    <TimePicker
                      sx={{ ml: 2 }}
                      value={dailyTime}
                      onChange={addDailyTime}
                      disableContainer
                      disablePadding
                    />
                    {false && (
                      <Button
                        variant="outlined"
                        startIcon={<AddAlarmRounded />}
                        sx={{ ml: 2 }}
                        onClick={addDailyTime}
                        disabled={!dailyTime}
                      >
                        Add time
                      </Button>
                    )}
                  </Grid>
                  <Grid item container alignItems={'center'}>
                    {dailyOccurrences.map((item) => (
                      <Chip
                        label={item.text}
                        key={item._id}
                        sx={{ m: 0.5 }}
                        onDelete={() => setDailyOccurrences(dailyOccurrences.filter((x) => x._id !== item._id))}
                      />
                    ))}
                  </Grid>
                </>
              )}

              {schedulingOption === 'hourly' && (
                <Grid item container alignItems={'center'} sx={{ mt: 1 }}>
                  <Typography>Send emails every</Typography>
                  <TextField
                    type="number"
                    sx={{ mx: 1, width: 80 }}
                    value={hoursInterval}
                    onChange={(e) => {
                      const value = e.target.value === '' ? '' : Number(e.target.value);
                      if (value !== '' && value < 1) return;

                      setHoursInterval(value);
                    }}
                    variant="standard"
                  />
                  <Typography>hour{hoursInterval === 1 ? '' : 's'}</Typography>
                </Grid>
              )}

              <Grid item container alignItems={'center'} sx={{ mt: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={onlyInWorkhours} />}
                  label="Email only during normal working hours (Mon-Fri 8am-5pm)"
                  onChange={(e) => setOnlyInWorkhours(e.target.checked)}
                />
              </Grid>
            </Grid>

            {settingsChanged && (
              <Grid item container sx={{ mt: 1 }}>
                <Button
                  disabled={!schedulingSettingsValid}
                  variant="contained"
                  onClick={saveSchedulingSettings}
                  startIcon={
                    savingSchedulingSettings ? <CircularProgress size={24} sx={{ color: 'white' }} /> : <SaveRounded />
                  }
                >
                  save
                </Button>
              </Grid>
            )}
          </Grid>

          <Grid item container alignItems={'center'} sx={{ mt: 4 }}>
            <Typography variant="subtitle1">Notifications to email</Typography>
          </Grid>

          <Grid item container sx={{ mt: 1 }} alignItems={'center'}>
            <Alert severity="info">You can select what notification types will be emailed to you</Alert>

            <Button variant="outlined" startIcon={<BlockIcon />} sx={{ mr: 1, ml: 'auto' }} onClick={disableAllTypes}>
              Disable all
            </Button>
            <Button variant="outlined" startIcon={<DoneIcon />} onClick={enableAllTypes}>
              Enable all
            </Button>
          </Grid>

          <Grid container justifyContent="center">
            <Grid container item direction="column">
              {settings.map((setting) => (
                <Grid
                  key={setting._id}
                  item
                  container
                  style={{ marginTop: '1rem', padding: '0.5rem 0', borderTop: '1px solid #d0d0d0aa' }}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Grid item container style={{ width: '70%' }} direction="row" alignItems="center">
                    <Grid item style={{ marginRight: '1rem' }}>
                      {getIcon(setting.notificationCategory)}
                    </Grid>
                    <Grid item container direction="column" sm={10}>
                      <Typography variant="h6" style={{ textTransform: 'capitalize' }}>
                        {setting.notificationCategory}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: (t) => (t.palette.mode === 'light' ? '#353940' : '#f4f4f4'), fontSize: '14px' }}
                      >
                        {descriptions[setting.notificationCategory]}
                      </Typography>
                    </Grid>
                  </Grid>
                  <ButtonGroup variant="outlined">
                    <Button
                      variant={setting.enabled ? 'contained' : 'outlined'}
                      onClick={() => {
                        switchSetting(setting.notificationCategory, true);
                      }}
                    >
                      On
                    </Button>
                    <Button
                      variant={setting.enabled ? 'outlined' : 'contained'}
                      onClick={() => {
                        switchSetting(setting.notificationCategory, false);
                      }}
                    >
                      Off
                    </Button>
                  </ButtonGroup>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
}
